// Adopted and modified solution from Bohdan Didukh (2017)
// https://stackoverflow.com/questions/41594997/ios-10-safari-prevent-scrolling-behind-a-fixed-overlay-and-maintain-scroll-posi

export interface BodyScrollOptions {
  reserveScrollBarGap?: boolean
  allowTouchMove?: (el: any) => boolean
}

interface Lock {
  targetElement: any
  options: BodyScrollOptions
}

// Older browsers don't support event options, feature detect it.
let hasPassiveEvents = false
if (typeof window !== 'undefined') {
  const passiveTestOptions = {
    get passive () {
      hasPassiveEvents = true
      return undefined
    },
  }
  window.addEventListener('testPassive', () => { /* do nothing */ }, passiveTestOptions)
  window.removeEventListener('testPassive', () => { /* do nothing */ })
}

const isIosDevice =
  typeof window !== 'undefined' &&
  window.navigator &&
  window.navigator.platform &&
  /iP(ad|hone|od)/.test(window.navigator.platform)
// const isIosDevice = true
type HandleScrollEvent = TouchEvent

let locks: Array<Lock> = []
let documentListenerAdded = false
let initialClientY = -1
let previousBodyOverflowSetting: CSSStyleDeclaration['overflow'] | undefined
let previousBodyPaddingRight: CSSStyleDeclaration['paddingRight'] | undefined
let firstMove = false
let syntheticScroll = false

// returns true if `el` should be allowed to receive touchmove events.
const allowTouchMove = (el: EventTarget): boolean => {
  return locks.some((lock) => {
    if (lock.options.allowTouchMove && lock.options.allowTouchMove(el)) {
      return true
    }

    return false
  })
}

const preventDefault = (rawEvent: HandleScrollEvent): boolean => {
  const e = rawEvent || window.event

  // For the case whereby consumers adds a touchmove event listener to document.
  // Recall that we do document.addEventListener('touchmove', preventDefault, { passive: false })
  // in disableBodyScroll - so if we provide this opportunity to allowTouchMove, then
  // the touchmove event on document will break.
  if (e.target && allowTouchMove(e.target)) {
    return true
  }

  // Do not prevent if the event has more than one touch (usually meaning this is a multi touch gesture like pinch to zoom).
  if (e.touches.length > 1) { return true }

  if (e.preventDefault) { e.preventDefault() }

  return false
}

const setOverflowHidden = (options?: BodyScrollOptions) => {
  // Setting overflow on body/documentElement synchronously in Desktop Safari slows down
  // the responsiveness for some reason. Setting within a setTimeout fixes this.
  setTimeout(() => {
    // If previousBodyPaddingRight is already set, don't set it again.
    if (previousBodyPaddingRight === undefined) {
      const reserveScrollBarGap = !!options && options.reserveScrollBarGap === true
      const scrollBarGap = window.innerWidth - document.documentElement.clientWidth

      if (reserveScrollBarGap && scrollBarGap > 0) {
        previousBodyPaddingRight = document.body.style.paddingRight
        document.body.style.paddingRight = `${ scrollBarGap }px`
      }
    }

    // If previousBodyOverflowSetting is already set, don't set it again.
    if (previousBodyOverflowSetting === undefined) {
      previousBodyOverflowSetting = document.documentElement.style.overflow
      document.documentElement.style.overflow = 'hidden'
    }
  })
}

const restoreOverflowSetting = () => {
  // Setting overflow on body/documentElement synchronously in Desktop Safari slows down
  // the responsiveness for some reason. Setting within a setTimeout fixes this.
  setTimeout(() => {
    if (previousBodyPaddingRight !== undefined) {
      document.body.style.paddingRight = previousBodyPaddingRight

      // Restore previousBodyPaddingRight to undefined so setOverflowHidden knows it
      // can be set again.
      previousBodyPaddingRight = undefined
    }

    if (previousBodyOverflowSetting !== undefined) {
      document.documentElement.style.overflow = previousBodyOverflowSetting

      // Restore previousBodyOverflowSetting to undefined
      // so setOverflowHidden knows it can be set again.
      previousBodyOverflowSetting = undefined
    }
  })
}

// https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight#Problems_and_solutions
const isTargetElementTotallyScrolled = (targetElement: any): boolean =>
  targetElement ? targetElement.scrollHeight - targetElement.scrollTop <= targetElement.clientHeight : false

const handleScroll = (event: HandleScrollEvent, targetElement: HTMLElement, initialScroll: number): boolean => {
  const clientY = event.targetTouches[0].clientY - initialClientY

  if (event.target && allowTouchMove(event.target)) {
    return false
  }

  if (targetElement && targetElement.scrollTop === 0 && clientY > 0) {
    if (firstMove) {
      syntheticScroll = true
    }
    // element is at the top of its scroll.
    return preventDefault(event)
  }

  if (isTargetElementTotallyScrolled(targetElement) && clientY < 0) {
    if (firstMove) {
      syntheticScroll = true
    }
    // element is at the top of its scroll.
    return preventDefault(event)
  }

  if (syntheticScroll) {
    targetElement.scrollTop = initialScroll - clientY
  }

  event.stopPropagation()
  return true
}

export const disableBodyScroll = (targetElement: any, options?: BodyScrollOptions): void => {
  if (isIosDevice) {
    // targetElement must be provided, and disableBodyScroll must not have been
    // called on this targetElement before.
    if (!targetElement) {
      // eslint-disable-next-line no-console
      // console.error(
      //   'disableBodyScroll unsuccessful - targetElement must be provided when calling disableBodyScroll on IOS devices.'
      // )
      return
    }

    if (targetElement && !locks.some((lock) => lock.targetElement === targetElement)) {
      const lock = {
        targetElement,
        options: options || {},
      }

      locks = [...locks, lock]

      let initialScroll = 0
      targetElement.ontouchstart = (event: HandleScrollEvent) => {
        if (event.targetTouches.length === 1) {
          // detect single touch.
          firstMove = true
          syntheticScroll = false
          initialScroll = targetElement.scrollTop
          initialClientY = event.targetTouches[0].clientY
        }
      }
      targetElement.ontouchmove = (event: HandleScrollEvent) => {
        if (event.targetTouches.length === 1) {
          // detect single touch.
          handleScroll(event, targetElement, initialScroll)
          firstMove = false
        }
      }

      if (!documentListenerAdded) {
        document.addEventListener('touchmove', preventDefault, hasPassiveEvents ? { passive: false } : undefined)
        documentListenerAdded = true
      }
    }
  } else {
    setOverflowHidden(options)
    const lock = {
      targetElement,
      options: options || {},
    }

    locks = [...locks, lock]
  }
}

export const clearAllBodyScrollLocks = (): void => {
  if (isIosDevice) {
    // Clear all locks ontouchstart/ontouchmove handlers, and the references.
    locks.forEach((lock: Lock) => {
      lock.targetElement.ontouchstart = null
      lock.targetElement.ontouchmove = null
    })

    if (documentListenerAdded) {
      document.removeEventListener('touchmove', preventDefault)
      documentListenerAdded = false
    }

    locks = []

    // Reset initial clientY.
    initialClientY = -1
  } else {
    restoreOverflowSetting()
    locks = []
  }
}

export const enableBodyScroll = (targetElement: any): void => {
  if (isIosDevice) {
    if (!targetElement) {
      // eslint-disable-next-line no-console
      // console.error(
      //   'enableBodyScroll unsuccessful - targetElement must be provided when calling enableBodyScroll on IOS devices.'
      // )
      return
    }

    targetElement.ontouchstart = null
    targetElement.ontouchmove = null

    locks = locks.filter((lock) => lock.targetElement !== targetElement)

    if (documentListenerAdded && locks.length === 0) {
      document.removeEventListener('touchmove', preventDefault)

      documentListenerAdded = false
    }
  } else {
    locks = locks.filter((lock) => lock.targetElement !== targetElement)
    if (!locks.length) {
      restoreOverflowSetting()
    }
  }
}

export interface WindowScrollPosition {
  scrollTop: number
  scrollLeft: number
}

export const scrollToElement = (
  element: Element | null,
  options?: ScrollIntoViewOptions
): void => {
  if (!element) {
    return
  }
  element.scrollIntoView(options ?? { behavior: 'smooth', block: 'start' })
}

export const getScrollPosition = (): WindowScrollPosition => {
  const docEl = document.documentElement
  const body = document.body
  return {
    scrollTop: window.pageYOffset ?? docEl.scrollTop ?? body.scrollTop,
    scrollLeft: window.pageXOffset ?? docEl.scrollLeft ?? body.scrollLeft,
  }
}

export const smoothScroll = (top: number, left = 0): void => {
  window.scrollTo({ top, left, behavior: 'smooth' })
}

export const isScrolledToBottom = (threshold = 1): boolean => {
  const docEl = document.documentElement
  const scrollTop = window.pageYOffset ?? docEl.scrollTop ?? document.body.scrollTop
  const scrollHeight = docEl.scrollHeight
  const clientHeight = window.innerHeight || docEl.clientHeight
  return scrollTop + clientHeight >= scrollHeight - threshold
}
